import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { therapistService } from '@leoclinic/services';
import type {
  TherapistWithProfile,
  TherapistFormData,
  SearchFilters,
  PaginatedResponse,
  WorkingHour,
  WorkingHourFormData,
  BlockedPeriod,
  BlockedPeriodFormData,
  Profile,
} from '@leoclinic/types';

const THERAPISTS_KEY = ['therapists'] as const;

export function useTherapists(clinicId: string | null, filters: SearchFilters = {}) {
  return useQuery<PaginatedResponse<TherapistWithProfile>>({
    queryKey: [...THERAPISTS_KEY, 'list', clinicId, filters],
    queryFn: () => therapistService.list(clinicId!, filters),
    enabled: !!clinicId,
    staleTime: 30 * 1000,
  });
}

export function useTherapist(id: string | null) {
  return useQuery<TherapistWithProfile | null>({
    queryKey: [...THERAPISTS_KEY, 'detail', id],
    queryFn: () => therapistService.getById(id!),
    enabled: !!id,
  });
}

export function useAvailableProfiles(clinicId: string | null) {
  return useQuery<Profile[]>({
    queryKey: [...THERAPISTS_KEY, 'available-profiles', clinicId],
    queryFn: () => therapistService.getAvailableProfiles(clinicId!),
    enabled: !!clinicId,
  });
}

export function useWorkingHours(therapistId: string | null) {
  return useQuery<WorkingHour[]>({
    queryKey: [...THERAPISTS_KEY, 'hours', therapistId],
    queryFn: () => therapistService.getWorkingHours(therapistId!),
    enabled: !!therapistId,
  });
}

export function useBlockedPeriods(therapistId: string | null) {
  return useQuery<BlockedPeriod[]>({
    queryKey: [...THERAPISTS_KEY, 'blocked', therapistId],
    queryFn: () => therapistService.getBlockedPeriods(therapistId!),
    enabled: !!therapistId,
  });
}

export function useTherapistsOnDuty(clinicId: string | null) {
  return useQuery<{ id: string; full_name: string }[]>({
    queryKey: [...THERAPISTS_KEY, 'on-duty', clinicId],
    queryFn: () => therapistService.getOnDutyTherapists(clinicId!),
    enabled: !!clinicId,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });
}

export function useTherapistMutations(clinicId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: THERAPISTS_KEY });

  const createMutation = useMutation({
    mutationFn: (data: TherapistFormData) => therapistService.create(clinicId!, data),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TherapistFormData> }) =>
      therapistService.update(id, data),
    onSuccess: invalidate,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => therapistService.archive(id),
    onSuccess: invalidate,
  });

  const uploadMutation = useMutation({
    mutationFn: ({ therapistId, file }: { therapistId: string; file: File }) =>
      therapistService.uploadPhoto(clinicId!, therapistId, file),
    onSuccess: invalidate,
  });

  const saveHoursMutation = useMutation({
    mutationFn: ({ therapistId, hours }: { therapistId: string; hours: WorkingHourFormData[] }) =>
      therapistService.saveWorkingHours(clinicId!, therapistId, hours),
    onSuccess: invalidate,
  });

  const addBlockedMutation = useMutation({
    mutationFn: ({ therapistId, data }: { therapistId: string; data: BlockedPeriodFormData }) =>
      therapistService.addBlockedPeriod(clinicId!, therapistId, data),
    onSuccess: invalidate,
  });

  const removeBlockedMutation = useMutation({
    mutationFn: (id: string) => therapistService.removeBlockedPeriod(id),
    onSuccess: invalidate,
  });

  return {
    createTherapist: createMutation.mutateAsync,
    updateTherapist: updateMutation.mutateAsync,
    archiveTherapist: archiveMutation.mutateAsync,
    uploadTherapistPhoto: uploadMutation.mutateAsync,
    saveWorkingHours: saveHoursMutation.mutateAsync,
    addBlockedPeriod: addBlockedMutation.mutateAsync,
    removeBlockedPeriod: removeBlockedMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isSavingHours: saveHoursMutation.isPending,
  };
}

export { THERAPISTS_KEY };
